import { Inertia } from '@inertiajs/inertia'
import { Component, h, Host, Prop } from '@stencil/core'
import { css } from 'src/lib/emotion'

import { IconName } from '../../icons'
import { OpacityType } from '../../tailwind'
import { colorClasses } from '../../utils/css'
import { classes } from '../../utils/css'
import { setFormRef } from '../../utils/form'

const loading = css`
  pointer-events: none;
  color: transparent;
  & > :not(ui-spinner) {
    visibility: hidden;
  }
`

@Component({
  tag: 'ui-button',
})
export class Button {
  @Prop() name: string
  @Prop() value: string | number | string[]
  @Prop() form: string
  @Prop() formAction: string
  @Prop() formMethod: string
  @Prop() type: 'button' | 'submit' = 'button'
  @Prop() disabled: boolean
  @Prop() href: string
  @Prop() download = false
  @Prop() target: string
  @Prop() align: 'center' | 'start' | 'end' = 'center'
  @Prop() rounded: 'md' | 'lg' | 'xl' | '2xl' | 'full' = 'md'
  @Prop() inertia: boolean

  @Prop() icon: IconName
  @Prop() iconStyle: Partial<CSSStyleDeclaration>
  @Prop() iconRight: boolean

  @Prop() labelStyle: Partial<CSSStyleDeclaration>

  @Prop() color = 'surface-highest'
  @Prop() bgColor: string
  @Prop() hoverColor: string
  @Prop() progressColor = 'black'

  @Prop() small = false
  @Prop() large = false
  @Prop() larger = false
  @Prop() uppercase = true

  @Prop() spinner: boolean
  @Prop() pressed: boolean

  @Prop() progressPct = 0
  @Prop() progressOpacity: OpacityType = '50'

  get defaultSize() {
    return !this.small && !this.large && !this.larger
  }

  render() {
    const Tag = this.href ? 'a' : 'button'
    return (
      <Host
        class={`
        relative inline-flex cursor-pointer
        ${classes(this.uppercase, 'leading-none')}
        ${classes(this.defaultSize, 'text-sm')}
        ${classes(this.small, 'text-xs')}
        ${classes(this.large, 'text-base font-bold')}
        ${classes(this.larger, 'text-lg font-extrabold')}
        ${classes(this.disabled, 'pointer-events-none opacity-50')}
      `}
      >
        <Tag
          ref={setFormRef<HTMLAnchorElement | HTMLButtonElement>(this.form)}
          {...(this.href
            ? {
                href: this.href,
                download: this.download ? '' : null,
                target: this.target,
                onClick: (event) => {
                  if (this.inertia) {
                    event.preventDefault()
                    Inertia.visit(this.href)
                  }
                },
              }
            : {
                name: this.name,
                value: this.value,
                type: this.form ? 'submit' : this.type,
                disabled: this.disabled,
                formAction: this.formAction,
                formMethod: this.formMethod,
                formTarget: this.target,
              })}
          class={`
            tracking-wide w-full truncate flex-grow focus:shadow-outline
            justify-${this.align} rounded-${this.rounded} transition duration-200
            relative flex items-center border
            ${classes(this.uppercase, 'uppercase leading-none')}
            ${classes(this.spinner, loading)}
            ${classes(this.defaultSize, 'px-12px py-10px font-semibold')}
            ${classes(this.small, 'px-10px py-8px font-semibold')}
            ${classes(this.large, 'px-14px py-12px font-bold')}
            ${classes(this.larger, 'px-4 py-3 font-extrabold')}
            ${colorClasses(
              {
                border: this.color,
                text: this.hoverColor
                  ? this.color
                  : this.pressed
                  ? this.bgColor
                    ? this.bgColor
                    : 'surface-lower'
                  : this.color,
                bg: this.pressed ? this.color : this.bgColor,
              },
              {
                hover: {
                  border: this.hoverColor ? this.hoverColor : this.color,
                  text: this.hoverColor ? this.hoverColor : this.bgColor ? this.bgColor : 'surface-lower',
                  bg: this.pressed ? this.bgColor : this.hoverColor ? this.bgColor : this.color,
                },
              }
            )}
          `}
          style={{ outline: 'none' }}
        >
          <div
            class={`
              absolute top-0 bottom-0 left-0
              opacity-${this.progressOpacity}
              ${colorClasses({ bg: this.progressColor })}
            `}
            style={{ width: this.progressPct * 100 + '%' }}
          />
          {this.icon && (
            <ui-icon
              icon={this.icon}
              class={`
                -mt-1px
                ${classes(this.iconRight, 'order-1 ml-5px', 'mr-5px')}
              `}
              style={this.iconStyle as any}
            />
          )}
          <span class={`truncate`} style={this.labelStyle as any}>
            <slot />
          </span>
          {this.spinner && <ui-spinner class={`absolute ${colorClasses({ text: this.color })}`} />}
          <slot name='tooltip' />
        </Tag>
      </Host>
    )
  }
}
